import {
  Component,
  forwardRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  input,
  contentChild,
  computed,
  signal,
  booleanAttribute,
  InputSignal,
  InputSignalWithTransform,
  Signal,
  WritableSignal
} from '@angular/core';

import { BLOCKABLE } from '../../domain/blockable.interface';
import { SpinnerComponent } from '../loading-indicator/spinner.component';
import { IconComponent } from '../icon/icon.component';
import type { Blockable } from '../../domain/blockable.interface';
import type { Type } from '@angular/core';

export const BUTTON_TYPES = [
  'primary',
  'secondary',
  'secondary-clear',
  'tertiary',
  'tertiary-clear',
  'quaternary',
  'destructive',
  'destructive-secondary',
  'marketing',
  'icon',
  'chip'
] as const;
export type ButtonType = typeof BUTTON_TYPES[number]
export type ButtonSizeType = 'large' | 'regular' | 'small' | 'xs' | 'inline'

export type ButtonAttrType = 'button' | 'submit' | null;

@Component({
  imports: [
    SpinnerComponent
  ],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[tdWbButton]',
  exportAs: 'tdWbButton',
  templateUrl: 'button.component.html',
  styleUrls: ['button.component.scss'],
  providers: [{
    provide: BLOCKABLE,
    useExisting: forwardRef((): Type<ButtonComponent> => ButtonComponent)
  }],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.td-wb-button]': 'true',
    '[class.td-wb-button--small]': 'buttonSize() === "small"',
    '[class.td-wb-button--xs]': 'buttonSize() === "xs"',
    '[class.td-wb-button--regular]': 'isRegular()',
    '[class.td-wb-button--large]': 'buttonSize() === "large"',
    '[class.td-wb-button--inline]': 'buttonSize() === "inline"',
    '[class.td-wb-button--primary]': 'buttonType() === "primary" || buttonType() === undefined',
    '[class.td-wb-button--secondary]': 'buttonType() === "secondary"',
    '[class.td-wb-button--secondary-clear]': 'buttonType() === "secondary-clear"',
    '[class.td-wb-button--tertiary]': 'buttonType() === "tertiary"',
    '[class.td-wb-button--quaternary]': 'buttonType() === "quaternary"',
    '[class.td-wb-button--destructive]': 'buttonType() === "destructive"',
    '[class.td-wb-button--destructive-secondary]': 'buttonType() === "destructive-secondary"',
    '[class.td-wb-button--marketing]': 'buttonType() === "marketing"',
    '[class.td-wb-button--icon]': 'buttonType() === "icon"',
    '[class.td-wb-button--chip]': 'buttonType() === "chip"',
    '[class.td-wb-button--chip-with-icon]': 'isChipWithIcon()',
    '[class.td-wb-button--spinner]': 'showSpinner()',
    '[attr.type]': 'attrType()',
    '[attr.disabled]': 'disabled() ? "" : null'
  }
})
export class ButtonComponent implements Blockable {

  readonly buttonType: InputSignal<ButtonType | undefined> = input<ButtonType | undefined>('primary', { alias: 'tdWbButtonType' });
  readonly buttonSize: InputSignal<ButtonSizeType> = input<ButtonSizeType>('regular', { alias: 'tdWbButtonSize' });
  readonly disabled: InputSignalWithTransform<boolean, boolean | string> = input<boolean, boolean | string>(false, { transform: booleanAttribute });
  readonly attrType: InputSignal<ButtonAttrType> = input<ButtonAttrType>('button', { alias: 'type' });

  readonly iconComponent: Signal<IconComponent | undefined> = contentChild(IconComponent);
  readonly showSpinner: WritableSignal<boolean> = signal(false);

  protected readonly isRegular: Signal<boolean> = computed(() => {
    const size = this.buttonSize();
    return (size === 'regular' || size === undefined) && this.buttonType() !== 'icon';
  });

  protected readonly isChipWithIcon: Signal<boolean> = computed(() => {
    return this.buttonType() === 'chip' && !!this.iconComponent();
  });

  /**
   * See Blockable.setBlocked. Called from tdWbBlock
   *
   * @param value
   */
  setBlocked(value: boolean): void {
    this.showSpinner.set(value);
  }

  isBlocked(): boolean {
    return this.showSpinner();
  }
}
